import { Button } from "@/components/button";
import { useProModal } from "@/components/pro-alert-box";
import Sidebar from "@/components/sidebar";
import { Table } from "@/components/table";
import { api } from "@/config/api-fetch";
import { KbType, useKnowledgeBase, useKnowledgeBaseStats } from "@/hooks/use-knowledge-base";
import { useModal } from "@/hooks/use-modal";
import { usePosts } from "@/hooks/use-posts";
import { useParentPosts } from "@/hooks/use-parent-posts";
import { useRoute } from "@/hooks/use-route";
import { IS_PRO, LIMITS, OPTIONS, POST_TYPES } from "@/utils/constants";
import { css } from "@/utils/css";
import { getPaginatedList } from "@/utils/paginate";
import { useMemo, useState, useCallback } from "@wordpress/element";
import { __ } from "@wordpress/i18n";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { phpDate } from "@/utils/date";

const statuses = [
    {
        value: "all",
        label: __("All", "antimanual"),
    },
    {
        value: "added",
        label: __("Added", "antimanual"),
    },
    {
        value: "not_added",
        label: __("Not Added", "antimanual"),
    },
    {
        value: "requires_update",
        label: __("Requires Update", "antimanual"),
    },
];

const postTypes = POST_TYPES.filter(pt => pt.hasEditor);

export function WordPressPosts() {
    const { query, updateQuery } = useRoute();

    const selectedPostType = useMemo(() => {
        const type = POST_TYPES.find((type) => type.id === query.atml_post_type);
        return type || POST_TYPES[0];
    }, [query.atml_post_type]);

    const selectedStatus = useMemo(() => {
        const status = statuses.find((status) => status.value === query.kb_status);
        return (status || statuses[0]).value;
    }, [query.kb_status]);

    const page = useMemo(() => {
        return query.page_no ? parseInt(query.page_no, 10) : 1;
    }, [query.page_no]);

    const limit = useMemo(() => {
        return query.limit ? parseInt(query.limit, 10) : OPTIONS.PAGINATION_LIMIT;
    }, [query.limit]);

    const search = query.search || "";
    const orderby = query.orderby || "";
    const order = (query.order as any) || "";
    const selectedParentPost = query.parent_post ? parseInt(query.parent_post, 10) : null;

    // Dynamically check if selected post type supports parent filtering
    const isHierarchical = selectedPostType?.isHierarchical ?? false;

    // Fetch parent posts for hierarchical post types
    const { data: parentPosts, isLoading: parentPostsLoading } = useParentPosts(selectedPostType.id, isHierarchical);

    // Build parent post options for the filter dropdown
    const parentPostOptions = useMemo(() => {
        const options = [
            { value: "", label: __("Any Parent", "antimanual") },
        ];
        parentPosts?.forEach((post) => {
            options.push({ value: `${post.id}`, label: post.title });
        });
        return options;
    }, [parentPosts]);

    const { data: stats, refetch: refetchStats } = useKnowledgeBaseStats();
    const { data: kbPosts, refetch, isLoading: kbPostsLoading } = useKnowledgeBase(KbType.wp, selectedPostType.id, -1);
    const { data, isLoading } = usePosts(selectedPostType.id, -1, {
        post_parent: isHierarchical ? selectedParentPost : null,
    });
    const [postsOnAdd, setPostsOnAdd] = useState<string[]>([]);
    const [postsOnUpdate, setPostsOnUpdate] = useState<string[]>([]);
    const [postsOnDelete, setPostsOnDelete] = useState<string[]>([]);

    const { posts } = useMemo(() => {
        const posts: { [key: string]: any[] } = {
            all: [],
            added: [],
            not_added: [],
            requires_update: [],
        };

        data?.posts?.forEach((originalPost) => {
            const kbPost = kbPosts?.find((kb) => kb.post_id === originalPost.ID);

            const post = {
                ...originalPost,
                isAdded: false,
                requiresUpdate: false,
            };

            posts.all.push(post);

            if (kbPost) {
                post.isAdded = true;
                posts.added.push(post);

                if (new Date(kbPost.modified).getTime() < new Date(originalPost.post_modified_gmt).getTime()) {
                    post.requiresUpdate = true;
                    posts.requires_update.push(post);
                }
            } else {
                posts.not_added.push(post);
            }
        });

        if (search) {
            Object.keys(posts).forEach((key) => {
                posts[key] = posts[key].filter((post) => 
                    post.post_title.toLowerCase().includes(search.toLowerCase())
                );
            });
        }

        return { posts };
    }, [data, kbPosts, search]);

    const { confirm } = useModal();
    const { showProAlert } = useProModal();

    const checkAndHandlePro = useCallback(() => {
        if (IS_PRO) return false;

        const total = stats?.find((stat) => stat.type === "wp")?.total || 0;
        const limit = LIMITS.KB_WP_POSTS;

        if (total >= limit) {
            showProAlert(
                __("Limit Exceeded", "antimanual") + ` (${total}/${limit})`,
                __(
                    "You have reached the limit of adding posts to the Knowledge Base. Upgrade to Pro to add more posts in the Knowledge Base.",
                    "antimanual"
                )
            );
            return true;
        }

        return false;
    }, [stats]);

    type Param = {
        knowledge_id: string;
        post_id: string;
    };

    const handleKbDelete = useCallback(
        async (ids: Param[]) => {
            if (!ids.length) return;

            const confirmed = await confirm(__("Are you sure you want to delete the item(s) from the Knowledge Base?", "antimanual"));
            if (!confirmed) return;

            await Promise.all(
                ids.map(async ({ knowledge_id, post_id }) => {
                    setPostsOnDelete((prev) => [...prev, post_id]);

                    try {
                        await api.delete("/knowledge-base?knowledge_id=" + knowledge_id);
                        await refetch().catch(() => {});
                        await refetchStats().catch(() => {});
                        toast.success(__("Successfully removed from Knowledge Base", "antimanual"));
                    } catch (e: any) {
                        toast.error(e?.message || __("Failed to remove from Knowledge Base", "antimanual"));
                    }

                    setPostsOnDelete((prev) => prev.filter((pid) => pid !== post_id));
                })
            );
        },
        [confirm, refetch, refetchStats]
    );

    const handleKbUpdate = useCallback(
        async (ids: Param[]) => {
            if (!ids.length) return;

            await Promise.all(
                ids.map(async ({ knowledge_id, post_id }) => {
                    setPostsOnUpdate((prev) => [...prev, post_id]);

                    try {
                        await api.delete("/knowledge-base?knowledge_id=" + knowledge_id);
                        await api.post("/knowledge-base/wp", { post_id });
                        await refetch().catch(() => {});
                        await refetchStats().catch(() => {});

                        toast.success(__("Successfully updated Knowledge Base", "antimanual"));
                    } catch (e: any) {
                        toast.error(e?.message || __("Failed to update Knowledge Base", "antimanual"));
                    }

                    setPostsOnUpdate((prev) => prev.filter((pid) => pid !== post_id));
                })
            );
        },
        [refetch, refetchStats]
    );

    const handleKbAdd = useCallback(
        async (ids: Param[]) => {
            if (checkAndHandlePro()) return;
            if (!ids.length) return;

            await Promise.all(
                ids.map(async ({ post_id }) => {
                    setPostsOnAdd((prev) => [...prev, post_id]);

                    try {
                        await api.post("/knowledge-base/wp", { post_id });
                        await refetch().catch(() => {});
                        await refetchStats().catch(() => {});
                        toast.success(__("Successfully added to Knowledge Base", "antimanual"));
                    } catch (e: any) {
                        toast.error(e?.message || __("Failed to add to Knowledge Base", "antimanual"));
                    }

                    setPostsOnAdd((prev) => prev.filter((pid) => pid !== post_id));
                })
            );
        },
        [refetch, checkAndHandlePro, refetchStats]
    );

    const resolveParamArray = useCallback(
        (post_ids: string[]) => {
            return post_ids.map((post_id) => {
                const kbPost = kbPosts?.find((kb) => kb.post_id === Number(post_id));
                return { post_id: post_id, knowledge_id: kbPost?.id || "" };
            });
        },
        [kbPosts]
    );

    const actions = useMemo(() => {
        const [add, update, remove] = [
            {
                value: "add-to-kb",
                label: __("Add to Knowledge Base", "antimanual"),
                handler: (post_ids: string[]) => {
                    handleKbAdd(resolveParamArray(post_ids));
                },
            },
            {
                value: "update-kb",
                label: __("Update Knowledge Base", "antimanual"),
                handler: (post_ids: string[]) => {
                    handleKbUpdate(resolveParamArray(post_ids));
                },
            },
            {
                value: "remove-from-kb",
                label: __("Remove from Knowledge Base", "antimanual"),
                handler: (post_ids: string[]) => {
                    handleKbDelete(resolveParamArray(post_ids));
                },
            },
        ];

        if (selectedStatus === statuses[1].value) {
            return [update, remove];
        }

        if (selectedStatus === statuses[2].value) {
            return [add];
        }

        if (selectedStatus === statuses[3].value) {
            return [update, remove];
        }

        return [add, update, remove];
    }, [selectedStatus, handleKbAdd, handleKbUpdate, handleKbDelete, resolveParamArray]);

    const selectedAction = useMemo(() => {
        const action = actions.find((action) => action.value === query.kb_action);
        return (action || actions[0]).value;
    }, [query.kb_action, actions]);

    const columns = [
        {
            id: "title",
            label: __("Title", "antimanual"),
            sortable: true,
        },
        {
            id: "last_modified",
            label: __("Last Modified", "antimanual"),
            sortable: true,
        },
        {
            id: "added_in",
            label: __("Added In", "antimanual"),
            sortable: true,
        },
        {
            id: "status",
            label: __("Status", "antimanual"),
        },
        {
            id: "action",
            label: __("Action", "antimanual"),
        },
    ];

    const rows = useMemo(() => {
        let genericPosts = posts[selectedStatus] || [];

        // Sorting
        if (orderby && order) {
            genericPosts = [...genericPosts].sort((a, b) => {
                let aValue: any = "";
                let bValue: any = "";

                if (orderby === "title") {
                    aValue = a.post_title;
                    bValue = b.post_title;
                } else if (orderby === "last_modified") {
                    aValue = new Date(a.post_modified_gmt).getTime();
                    bValue = new Date(b.post_modified_gmt).getTime();
                } else if (orderby === "added_in") {
                    const kbPostA = kbPosts?.find((kb) => kb.post_id === a.ID);
                    const kbPostB = kbPosts?.find((kb) => kb.post_id === b.ID);
                    aValue = kbPostA?.modified ? new Date(kbPostA.modified).getTime() : 0;
                    bValue = kbPostB?.modified ? new Date(kbPostB.modified).getTime() : 0;
                } else if (orderby === "status") {
                    // Status sorting might be complex based on derived status
                    // For simplicity, maybe skip or implement simple logic
                    aValue = a.isAdded ? (a.requiresUpdate ? 3 : 2) : 1;
                    bValue = b.isAdded ? (b.requiresUpdate ? 3 : 2) : 1;
                }

                if (typeof aValue === "string") {
                    return order === "ASC" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
                }

                if (typeof aValue === "number") {
                    return order === "ASC" ? aValue - bValue : bValue - aValue;
                }

                return 0;
            });
        }

        const postsInCurrentPage = getPaginatedList(genericPosts, page, limit);

        return (
            postsInCurrentPage?.map((post) => {
                const kbPost = kbPosts?.find((kb) => kb.post_id === post.ID);
                const lastModified = post.post_modified_gmt ? phpDate(post.post_modified_gmt) : null;
                const addedIn = kbPost?.modified ? phpDate(kbPost.modified as string) : null;
                const status = post.isAdded ? (post.requiresUpdate ? statuses[3] : statuses[1]) : statuses[2];

                const post_id = post.ID.toString();
                const knowledge_id = kbPost?.id || "";

                return {
                    id: post_id,
                    title: (
                        <div className={css("column")}>
                            {post.post_title ? (
                                <a href={post.permalink} target="_blank">
                                    {post.post_title || __("(No Title)", "antimanual")}
                                </a>
                            ) : (
                                __("(No Title)", "antimanual")
                            )}

                            {(post.permalink || post.edit_link) && (
                                <div className={css("actions")}>
                                    <a href={post.edit_link} target="_blank">
                                        {__("Edit", "antimanual")}
                                    </a>
                                    |
                                    <a href={post.permalink} target="_blank">
                                        {__("View", "antimanual")}
                                    </a>
                                </div>
                            )}
                        </div>
                    ),
                    added_in: addedIn ? (
                        <div>
                            <div>{format(addedIn, "PP")}</div>
                            <div>{format(addedIn, "pp")}</div>
                        </div>
                    ) : null,
                    last_modified: lastModified ? (
                        <div>
                            <div>{format(lastModified, "PP")}</div>
                            <div>{format(lastModified, "pp")}</div>
                        </div>
                    ) : null,
                    status: (
                        <div className={css("column-kb_status")}>
                            <span className={css("status", status.value)}>{status.label}</span>
                        </div>
                    ),
                    action: post.isAdded ? (
                        <div className={css("flex item-center gap-1")}>
                            <Button
                                variant="secondary"
                                small
                                disabled={!post.requiresUpdate}
                                loading={postsOnUpdate.includes(post_id)}
                                onClick={() => handleKbUpdate([{ post_id, knowledge_id }])}
                            >
                                {__("Update", "antimanual")}
                            </Button>
                            <Button
                                variant="danger"
                                small
                                loading={postsOnDelete.includes(post_id)}
                                onClick={() => handleKbDelete([{ post_id, knowledge_id }])}
                            >
                                {__("Remove", "antimanual")}
                            </Button>
                        </div>
                    ) : (
                        <Button small loading={postsOnAdd.includes(post_id)} onClick={() => handleKbAdd([{ post_id, knowledge_id }])}>
                            {__("Add to Knowledge Base", "antimanual")}
                        </Button>
                    ),
                };
            }) || []
        );
    }, [
        posts,
        kbPosts,
        selectedStatus,
        page,
        limit,
        search,
        orderby,
        order,
        postsOnAdd,
        postsOnUpdate,
        postsOnDelete,
        handleKbAdd,
        handleKbUpdate,
        handleKbDelete,
    ]);

    return (
        <div className={css("kb-option wp-posts")}>
            <Sidebar
                items={postTypes}
                selectedItem={selectedPostType}
                onSelectItem={(item) => {
                    updateQuery({ page: query.page, tab: query.tab, atml_post_type: item.id, parent_post: "" }, true);
                }}
                className="my-0 border-none"
            >
                <h3 className={css("mt-0")}>{selectedPostType.label}</h3>
                <ul className={css("filters")}>
                    {statuses.map((status) => {
                        return (
                            <li key={status.value}>
                                <a
                                    href=""
                                    className={css(status.value, selectedStatus === status.value && "active")}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        updateQuery({
                                            kb_status: status.value,
                                            page_no: "1",
                                        });
                                    }}
                                >
                                    {status.label}
                                    <span className={css("count")}>({posts[status.value]?.length || 0})</span>
                                </a>
                            </li>
                        );
                    })}
                </ul>
                <Table
                    key={`${selectedPostType.id}-${selectedStatus}-${selectedParentPost ?? ''}`}
                    rows={rows}
                    columns={columns}
                    actions={actions}
                    rowsLoading={isLoading || kbPostsLoading}
                    selectedAction={selectedAction}
                    onActionSelect={(action) => updateQuery({ kb_action: action })}
                    pagination={{
                        total: posts?.[selectedStatus]?.length || 0,
                        limit: limit,
                        currentPage: page,
                        onPageChange: (page) => updateQuery({ page_no: `${page}` }),
                        onLimitChange: (limit) => updateQuery({ limit: `${limit}`, page_no: "1" }),
                    }}
                    search={search}
                    onSearch={(search) => updateQuery({ search, page_no: "1" })}
                    sorting={{ orderby, order }}
                    onSort={(orderby, order) => updateQuery({ orderby, order, page_no: "1" })}
                    filter={isHierarchical ? {
                        options: parentPostOptions,
                        value: selectedParentPost !== null ? `${selectedParentPost}` : "",
                        label: __("Filter by Parent", "antimanual"),
                        onChange: (value) => updateQuery({ parent_post: value || "", page_no: "1" }),
                    } : undefined}
                />
            </Sidebar>
        </div>
    );
}
